import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {AuthenticationService} from '../../services/auth/authentication.service';
import {ApiService} from '../../services/api/api.service';
import {FormatService} from '../../services/format/format.service';
import * as moment from 'moment';

declare const $: any;

@Component({
  selector: 'app-audio',
  templateUrl: './audio.component.html',
  styleUrls: ['./audio.component.css']
})
export class AudioComponent implements OnInit, OnDestroy {

  isForm: any;
  isGrouped: any = [];   // one row per surah (server-side GROUP BY)
  isExpanded: any = {};  // expand state per surah id
  isTotalAll: number = 0;

  isKeyword: string = '';
  isSurah: string = '';

  isPage: number = 1;
  isPageSize: number = 10;
  isPages: any = [];
  isTableLoading: boolean = false;

  attachment: any = null;
  dataSurah: any = [];

  isLoading = false;

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private authService: AuthenticationService,
    private apiService: ApiService,
    private formatService: FormatService,
  ) { }

  ngOnInit(): void {
    this.isPageSize = this.apiService.LIMIT || 10;
    this.resetForm();
    this.getSurah();
    this.getData();
  }

  ngOnDestroy(): void {
    if (this.player) { this.player.pause(); }
  }

  async resetForm() {
    this.attachment = null;
    this.isForm = this.fb.group({
      id: new FormControl(''),
      surah: new FormControl('', [Validators.required]),
      ayah: new FormControl('', [Validators.required, Validators.min(1)]),
      file: new FormControl('', [Validators.required]),
    })
  }

  async getSurah() {
    try {
      this.apiService.methodGetSilent(`surah`, this.authService.getToken()).subscribe((response) => {
        if (response && response.status == 200) {
          this.dataSurah = response.data;
        }
      })
    } catch (error) {
      console.log(error);
    }
  }

  surahName(id: any) {
    const s = this.dataSurah.find((d: any) => d.id == id);
    return s ? s.text : '';
  }

  private searchTimer: any;
  private lastQuery: string = '';

  onKeywordInput(value: string) {
    this.isKeyword = value;
    clearTimeout(this.searchTimer);
    // debounce: wait 450ms after the last keystroke before hitting the API
    this.searchTimer = setTimeout(() => this.applyFilter(), 450);
  }

  async applyFilter() {
    clearTimeout(this.searchTimer);
    const query = `${this.isKeyword}|${this.isSurah}`;
    if (query === this.lastQuery) { return; } // query unchanged, skip the request
    this.lastQuery = query;
    this.isPage = 1;
    this.isExpanded = {};
    await this.getData();
  }

  async onReset() {
    this.isGrouped = [];
    this.isExpanded = {};
    this.isPage = 1;
    this.isTotalAll = 0;
    this.isKeyword = '';
    this.isSurah = '';
    this.lastQuery = `${this.isKeyword}|${this.isSurah}`;
    await this.resetForm();
    await this.getData();
  }

  async getData() {
    let searchData = {"keyword": this.isKeyword, "surah": this.isSurah};
    const offset = (this.isPage - 1) * this.isPageSize;
    this.isTableLoading = true;
    try {
      this.apiService.methodGetSilent(`audio/surah/${offset}/${encodeURIComponent(JSON.stringify(searchData))}`, this.authService.getToken()).subscribe((response) => {
        this.isTableLoading = false;
        if (response && response.status == 200) {
          const prevGroups = this.isGrouped;
          this.isGrouped = [];
          (response.total ? response.data.forEach((row: any) => {
            // aliased aggregates: id/name/total_ayah from quran_surah, total_file, ayah = distinct count
            const prev = prevGroups.find((p: any) => p.id == row.id);
            const group = {
              id: row.id,
              name: row.name || '-',
              totalAyah: row.total_ayah || 0,
              ayahCount: row.ayah || 0,
              fileCount: row.total_file || 0,
              updatedVw: moment(row.updatedAt).locale('id').format("D MMMM YYYY HH:mm"),
              items: (prev ? prev.items : []), // member cache survives rebuilds (cleared after mutations)
              visible: [],
              loading: false,
            };
            this.applyItemFilter(group); // cached members must respect the current keyword too
            this.isGrouped.push(group);
          }) : false);
          this.isTotalAll = response.total || 0;
          this.buildPager();
          // an active search/filter means the user is looking for something specific —
          // open every matching surah right away instead of making them click each one
          if (this.isKeyword || this.isSurah) {
            this.isGrouped.forEach((g: any) => {
              this.isExpanded[g.id] = true;
              if (!g.items.length) { this.loadGroupItems(g); }
            });
          }
        }
      }, () => { this.isTableLoading = false; })
    } catch (error) {
      this.isTableLoading = false;
      console.log(error);
    }
  }

  // during a keyword search only matching files stay visible; if none of the
  // files match (the group matched by surah name/number), the whole list is relevant
  private applyItemFilter(g: any) {
    const keyword = this.isKeyword.trim().toLowerCase();
    if (!keyword) { g.visible = g.items; return; }
    const matched = g.items.filter((it: any) => String(it.file || '').toLowerCase().indexOf(keyword) >= 0);
    g.visible = matched.length ? matched : g.items;
  }

  // ===== expand / collapse =====
  loadGroupItems(g: any) {
    g.loading = true;
    return new Promise<void>((resolve) => {
      this.apiService.methodGetSilent(`audio/group-items/${g.id}`, this.authService.getToken()).subscribe((response) => {
        if (response && response.status == 200) {
          // an ayah can hold several files — number them within the ayah (Ayat 2 · 1, · 2, …)
          const perAyah: any = {};
          response.data.forEach((element: any) => { perAyah[element.ayah] = (perAyah[element.ayah] || 0) + 1; });
          const seen: any = {};
          response.data.forEach((element: any) => {
            if (!element.quran_surah) { element.quran_surah = {name: g.name}; }
            element.url = element.file ? `${response.directoryFile}audio/${element.file}` : '';
            element.updatedVw = moment(element.updatedAt).locale('id').format("D MMMM YYYY HH:mm");
            seen[element.ayah] = (seen[element.ayah] || 0) + 1;
            element.seq = seen[element.ayah];
            element.hasTwin = perAyah[element.ayah] > 1;
          });
          g.items = response.data;
          this.applyItemFilter(g);
        }
        g.loading = false;
        resolve();
      }, () => { g.loading = false; resolve(); });
    });
  }

  async toggleGroup(g: any) {
    if (!this.isExpanded[g.id] && !g.items.length) {
      await this.loadGroupItems(g);
    }
    this.isExpanded[g.id] = !this.isExpanded[g.id];
  }

  totalPages() {
    return Math.max(1, Math.ceil(this.isTotalAll / this.isPageSize));
  }
  buildPager() {
    const total = this.totalPages();
    const pages: any[] = [];
    for (let p = 1; p <= total; p++) {
      if (p == 1 || p == total || Math.abs(p - this.isPage) <= 1) {
        pages.push(p);
      } else if (pages[pages.length - 1] != '...') {
        pages.push('...');
      }
    }
    this.isPages = pages;
  }
  async goToPage(p: any) {
    const total = this.totalPages();
    if (p < 1 || p > total || p == this.isPage) { return; }
    this.isPage = p;
    await this.getData();
  }
  rowNo(i: number) {
    return (this.isPage - 1) * this.isPageSize + i + 1;
  }
  summaryFrom() {
    return this.isTotalAll ? (this.isPage - 1) * this.isPageSize + 1 : 0;
  }
  summaryTo() {
    return (this.isPage - 1) * this.isPageSize + this.isGrouped.length;
  }
  formatNumber(value: any) {
    return this.formatService.dotFormat(value || 0);
  }

  // wrap keyword matches in <mark> for the list; input is HTML-escaped first
  highlight(text: any) {
    const esc = String(text == null ? '' : text)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const keyword = this.isKeyword.trim();
    if (!keyword) { return esc; }
    const escapedKeyword = keyword
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // neutralize regex metacharacters
    return esc.replace(new RegExp(`(${escapedKeyword})`, 'gi'), '<mark class="vl-hl">$1</mark>');
  }

  // "Ayat 2 · 1" when the ayah holds several files, plain "Ayat 2" otherwise
  ayahLabel(v: any) {
    return `Ayat ${v.ayah}` + (v.hasTwin ? ` · ${v.seq}` : ``);
  }

  // ===== inline audio player: one shared element, play/pause toggles per row =====
  private player: any = null;
  playingId: any = 0;

  togglePlay(v: any) {
    if (!v.url) { return; }
    if (this.playingId === v.id) {
      this.player.pause();
      return; // the pause handler clears playingId
    }
    if (!this.player) {
      this.player = new Audio();
      this.player.onended = () => { this.playingId = 0; };
      this.player.onpause = () => { this.playingId = 0; };
    }
    this.player.src = v.url;
    const attempt = this.player.play();
    this.playingId = v.id;
    if (attempt && attempt.catch) {
      attempt.catch(() => {
        this.playingId = 0;
        this.toastr.error(`${v.file}`, `Gagal memutar audio`);
      });
    }
  }

  // ===== download: fetch as blob so the browser saves it with the file name
  // (a plain <a download> is ignored cross-origin by Spaces) =====
  isDownloadingId: any = 0;

  async downloadFile(v: any) {
    if (!v.url || this.isDownloadingId) { return; }
    this.isDownloadingId = v.id;
    try {
      const res = await fetch(v.url);
      if (!res.ok) { throw new Error(`${res.status}`); }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = v.file;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (error) {
      this.toastr.error(`${v.file}`, `Gagal mengunduh`);
    }
    this.isDownloadingId = 0;
  }

  // ===== delete confirmation (custom dialog, not SweetAlert) =====
  isConfirm: any = null; // {v}
  isDeleting: boolean = false;

  onDelete(v: any) {
    this.isConfirm = {v: v};
    document.body.style.overflow = 'hidden'; // lock the page scroll behind the dialog
    setTimeout(() => { // focus the Cancel button so the keyboard lands inside the dialog
      const btn: any = document.querySelector('.hds-dialog .vl-btn-ghost');
      if (btn) { btn.focus(); }
    });
  }

  closeConfirm() {
    if (this.isDeleting) { return; } // never close while a deletion is in flight
    this.isConfirm = null;
    document.body.style.overflow = '';
  }

  @HostListener('document:keydown.escape')
  onEscapeConfirm() {
    if (this.isConfirm) { this.closeConfirm(); }
  }

  // reload without discarding filter/page; caches & expands are stale after a mutation
  async refreshAfterMutation() {
    this.isGrouped = [];
    this.isExpanded = {};
    await this.getData();
    if (this.isGrouped.length === 0 && this.isPage > 1) {
      this.isPage = this.isPage - 1;
      await this.getData();
    }
  }

  async confirmDelete() {
    const c = this.isConfirm;
    if (!c || this.isDeleting) { return; }
    this.isDeleting = true;
    this.apiService.methodDeleteSilent(`audio/${c.v.id}`, this.authService.getToken()).subscribe(async (response) => {
      this.isDeleting = false;
      if (response && response.status == 200) {
        this.closeConfirm();
        await this.toastr.success(`${c.v.quran_surah.name} — ${this.ayahLabel(c.v)}`, `Audio dihapus`);
        this.refreshAfterMutation();
      } else {
        // failure: keep the dialog open so the user can retry
        await this.toastr.error((response && response.message) || `Coba lagi beberapa saat.`, `Gagal menghapus`);
      }
    }, async () => {
      this.isDeleting = false;
      await this.toastr.error(`Periksa koneksi lalu coba lagi.`, `Gagal menghapus`);
    });
  }

  // ===== form dialog =====
  async onShowAdd(surahId: any = '') {
    await this.resetForm();
    if (surahId) { // "+" on a group row: preset that surah
      this.isForm.patchValue({surah: surahId});
    }
    $('#formData').modal({backdrop: 'static', keyboard: false})
  }

  // show inline errors once a field is touched / after a submit attempt
  isInvalid(control: string) {
    const c = this.isForm.get(control);
    return c && c.invalid && (c.touched || c.dirty);
  }

  isEditLabel: string = ''; // which file is being edited, e.g. "Al-Baqarah — Ayat 2 · 1"

  async onEdit(v: any) {
    try {
      await this.resetForm();
      this.isEditLabel = `${v.quran_surah.name} — ${this.ayahLabel(v)}`;
      await this.isForm.patchValue({id: v.id, surah: v.surah, ayah: v.ayah});
      // editing: a new file is optional — the stored one stays if none is chosen
      this.isForm.controls['file'].clearValidators();
      this.isForm.controls['file'].updateValueAndValidity();
      // keyboard: false — Escape must never dismiss the dialog mid-save
      await $('#formData').modal({backdrop: 'static', keyboard: false, show: true});
    } catch (error) {
      console.log(error);
    }
  }

  async onFileSelect(e: any) {
    if (e.target.files.length > 0) {
      this.attachment = e.target.files[0];
    }
  }

  async onSubmit() {
    try {
      if (this.isForm.invalid) {
        this.isForm.markAllAsTouched();
        return;
      }
      if (this.isLoading) { return; }
      this.isLoading = true;
      // getRawValue: includes the surah control when it is locked/disabled
      const payload = this.isForm.getRawValue();
      const isEdit = !!payload.id;
      let formValue = new FormData();
      formValue.append('id', payload.id);
      formValue.append('admin_id', this.authService.getSession().id);
      formValue.append('surah', payload.surah);
      formValue.append('ayah', payload.ayah);
      // the file lives in this.attachment — never in the form control (see playbook §8)
      if (this.attachment instanceof File) {
        formValue.append('file', this.attachment);
      }
      const request = isEdit
        ? this.apiService.methodPutWithFormdata(`audio/${payload.id}`, formValue, this.authService.getToken())
        : this.apiService.methodPostWithFormdata(`audio`, formValue, this.authService.getToken());
      request.subscribe(async (response) => {
        this.isLoading = false;
        if (response && response.status == 200) {
          // close the modal only on success — on failure the user's input stays intact
          $('#formData').modal('hide');
          await this.toastr.success(
            `${this.surahName(payload.surah)} — Ayat ${payload.ayah}`,
            isEdit ? `Audio diupdate` : `Audio disimpan`);
          await this.refreshAfterMutation(); // keep the user's page & filter
        } else {
          await this.toastr.error((response && response.message) || `Periksa isian lalu coba lagi.`, `Gagal menyimpan`);
        }
      }, async () => {
        this.isLoading = false;
        await this.toastr.error(`Periksa koneksi lalu coba lagi.`, `Gagal menyimpan`);
      });
    } catch (error) {
      this.isLoading = false;
      await this.toastr.error(`Periksa isian lalu coba lagi.`, `Gagal menyimpan`);
    }
  }
}
